import { ALL_OPTIONS_VALUE, SORT_OPTIONS } from 'constants/common';

/**
 * @typedef {'asc' | 'desc'} SortOrder
 */

/**
 * Generates a formatted ordering value based on the sort order and the order-by attribute.
 *
 * @param {SortOrder} order - The order direction.
 * @param {string} orderBy - The attribute or field by which ordering is applied.
 * @param {function} formatOrderBy - A callback function to further format the `orderBy` value, accepting the attribute and prefix as arguments.
 * @returns {string} The formatted ordering value, combining potential prefixes and formatted order-by attribute.
 */
export const formatOrderingValue = (order, orderBy, formatOrderBy = (v) => v) => {
  const prefix = order === SORT_OPTIONS.ASC ? '' : '-';
  const formattedOrderBy = formatOrderBy(orderBy, prefix);

  return `${prefix}${formattedOrderBy}`;
};

/**
 * Determines the next sort order based on the current sort order.
 *
 * @param {SortOrder} currentOrder - The current sort order.
 * @returns {SortOrder} The next sort order.
 */
export const getListSortOrder = (currentOrder) =>
  currentOrder === SORT_OPTIONS.DESC ? SORT_OPTIONS.ASC : SORT_OPTIONS.DESC;

/**
 * Updates the filter model for a data grid by modifying or adding an entry for a specific column field.
 *
 * This function checks if the updated value for a specified column field exists in the filter model.
 * If the `updatedValue` is not provided or matches the predefined `ALL_OPTIONS_VALUE`,
 * the filter for the specified column field will be removed.
 * Otherwise, the function will either update the existing filter entry for the column or add a new filter entry.
 *
 * @param {Array<Object>} prev - The previous filter model array, where each entry is an object containing `field` and `value`.
 * @param {string} field - The key representing the column for which the filter is to be updated.
 * @param {any} updatedValue - The new filter value for the specified column. If null or `ALL_OPTIONS_VALUE`, the column's filter will be removed.
 * @returns {Array<Object>} - The updated filter model array after adding, updating, or removing the filter for the specified column field.
 */
export const updateDataGridFilterModel = (prev, field, updatedValue) => {
  if (!updatedValue || updatedValue === ALL_OPTIONS_VALUE || (Array.isArray(updatedValue) && !updatedValue.length)) {
    return prev.filter((item) => item.field !== field);
  }

  const isCurrentColumnFilterAdded = prev.some((item) => item.field === field);

  return isCurrentColumnFilterAdded
    ? prev.map((item) =>
        item.field === field
          ? {
              ...item,
              value: updatedValue,
            }
          : item,
      )
    : [
        ...prev,
        {
          field,
          value: updatedValue,
        },
      ];
};

/**
 * Filters a list of objects based on specified filter criteria.
 *
 * @function
 * @param {Array<Object>} list - The array of objects to be filtered.
 * @param {Array<Object>} filterModelItems - Array of filter criteria objects, where each object contains:
 *   - `field` {string}: The key of the object property to filter by.
 *   - `value` {Array<*>}: The array of permissible values for the specified key.
 * @param {Array<string>} excludedRowIds - Array of row IDs to be excluded from filtering.
 * @returns {Array<Object>} - A new array containing objects from the original list that match all filter criteria.
 * If the filterModelItems array is empty, the original list is returned.
 * Note: Rows with IDs in excludedRowIds are always included in the results.
 */
export const getFilteredList = (list, filterModelItems, excludedRowIds = []) =>
  list.filter(
    (row) =>
      excludedRowIds.includes(row.id) ||
      !filterModelItems.length ||
      filterModelItems.every((filter) => filter.value.includes(row[filter.field])),
  );
